import { Injectable } from '@angular/core';
import { BasketWebservice } from '@core/services/basket.webservice';
import { SuggestionId } from '@models/suggestion-id.type';
import { Action, NgxsOnInit, Selector, State, StateContext, StateToken } from '@ngxs/store';
import {
  AddToShoppingCart,
  ClearShoppingCart,
  GetShoppingCart,
  RemoveFromShoppingCart,
} from '@stores/shopping-cart/shopping-cart.action';
import { SuggestionState } from '@stores/suggestion/suggestion.state';
import { Suggestion } from '@wizbii/models';
import { map, tap } from 'rxjs/operators';

const ShoppingCartStateToken = new StateToken<ShoppingCartStateModel>('shoppingCart');

export type ShoppingCartStateModel = SuggestionId[];

@State({
  name: ShoppingCartStateToken,
  defaults: null,
})
@Injectable()
export class ShoppingCartState implements NgxsOnInit {
  @Selector([ShoppingCartState, SuggestionState.eligible])
  static articles(shoppingCart: ShoppingCartStateModel, eligibleSuggestions: Suggestion[]): Suggestion[] {
    return eligibleSuggestions?.filter((suggestion) => shoppingCart?.includes(suggestion.suggestionId));
  }

  @Selector([ShoppingCartState.articles])
  static totalPrice(suggestions: Suggestion[]): number {
    return suggestions?.reduce((acc, item) => acc + (item.premium.unitPrice || 0), 0) / 100;
  }

  @Selector([ShoppingCartState.articles])
  static totalTax(suggestions: Suggestion[]): number {
    return (
      suggestions?.reduce((acc, item) => acc + ((item.premium.unitPrice || 0) * (item.premium.taxRate || 0)) / 100, 0) /
      100
    );
  }

  @Selector([ShoppingCartState.articles])
  static currency(suggestions: Suggestion[]): string {
    return suggestions?.[0]?.currency;
  }

  constructor(readonly fibiiWebservice: BasketWebservice) {}

  ngxsOnInit(ctx?: StateContext<any>) {
    ctx?.dispatch(new GetShoppingCart());
  }

  @Action(GetShoppingCart)
  getShoppingCart(ctx: StateContext<ShoppingCartStateModel>) {
    return this.fibiiWebservice
      .getBasket()
      .pipe(map((response) => ctx.setState(response.articles.map((article) => article.suggestionId))));
  }

  @Action(AddToShoppingCart)
  addToShoppingCart(ctx: StateContext<ShoppingCartStateModel>, { article }: AddToShoppingCart) {
    const articles = ctx.getState();

    if (!articles) {
      return this.fibiiWebservice
        .createBasket([{ suggestionId: article.suggestionId }])
        .pipe(tap((response) => ctx.setState(response.articles.map((respArticle) => respArticle.suggestionId))));
    }

    return this.fibiiWebservice
      .addArticleToBasket(article.suggestionId)
      .pipe(tap((response) => ctx.setState([...articles, response.suggestionId])));
  }

  @Action(RemoveFromShoppingCart)
  removeFromShoppingCart(ctx: StateContext<ShoppingCartStateModel>, { article }: RemoveFromShoppingCart) {
    const newArticles = [...ctx.getState()].filter((item) => item !== article.suggestionId);
    return this.fibiiWebservice
      .removeArticleFromBasket(article.suggestionId)
      .pipe(tap(() => ctx.setState(newArticles)));
  }

  @Action(ClearShoppingCart)
  clearShoppingCart(ctx: StateContext<ShoppingCartStateModel>) {
    return this.fibiiWebservice.clearBasket().pipe(tap(() => ctx.setState([])));
  }
}
